<template>
  <div class="app-input-wrapper">
    <label 
      v-if="label" 
      :for="id" 
      class="app-input-label"
      :class="{ 'app-input-required': required }"
    >
      {{ label }}
      <span v-if="required" class="app-input-required-mark" aria-hidden="true">*</span>
    </label>
    
    <div 
      class="app-input-container" 
      :class="[
        containerClasses,
        {
          'app-input-focused': isFocused,
          'app-input-disabled': disabled,
          'app-input-error': !!error,
          'app-input-success': isValid && touched
        }
      ]"
    >
      <div v-if="prefixIcon || $slots.prefix" class="app-input-prefix">
        <i v-if="prefixIcon" :class="['app-input-icon', prefixIcon]" aria-hidden="true"></i>
        <slot v-else name="prefix"></slot>
      </div>
      
      <input
        v-if="type !== 'textarea'"
        ref="input"
        :id="id"
        :type="type"
        :name="name"
        :value="inputValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :autofocus="autofocus"
        :min="min"
        :max="max"
        :step="step"
        :autocomplete="autocomplete"
        :aria-invalid="!!error"
        :aria-describedby="getAriaDescribedBy"
        class="app-input"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @change="onChange"
        @keydown="onKeydown"
        @keyup.enter="onEnter"
      />
      
      <textarea
        v-else
        ref="input"
        :id="id"
        :name="name"
        :value="inputValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :autofocus="autofocus"
        :autocomplete="autocomplete"
        :rows="rows"
        :aria-invalid="!!error"
        :aria-describedby="getAriaDescribedBy"
        class="app-input app-textarea"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @change="onChange"
        @keydown="onKeydown"
      ></textarea>
      
      <div v-if="suffixIcon || clearable || $slots.suffix" class="app-input-suffix">
        <template v-if="clearable && inputValue">
          <transition name="fade">
            <span
              class="app-input-clear"
              @click="clearInput"
              @keydown.enter="clearInput"
              role="button"
              tabindex="0"
              aria-label="Clear input"
            >
              <i class="fas fa-times-circle"></i>
            </span>
          </transition>
        </template>
        <template v-else-if="suffixIcon">
          <i :class="['app-input-icon', suffixIcon]" aria-hidden="true"></i>
        </template>
        <template v-else>
          <slot name="suffix"></slot>
        </template>
      </div>
    </div>
    
    <div class="app-input-feedback">
      <transition name="fade">
        <small 
          v-if="hint && !error" 
          :id="`${id}-hint`"
          class="app-input-hint"
        >
          {{ hint }}
        </small>
      </transition>
      
      <transition name="fade">
        <small 
          v-if="error" 
          :id="`${id}-error`"
          class="app-input-error"
          role="alert"
        >
          {{ error }}
        </small>
      </transition>
      
      <transition name="fade">
        <small
          v-if="maxLength"
          :id="`${id}-counter`"
          class="app-input-counter"
          :class="{ 'app-input-counter-limit': counterExceeded }"
        >
          {{ inputValue?.length || 0 }}/{{ maxLength }}
        </small>
      </transition>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue';
import { useField } from 'vee-validate';

export default defineComponent({
  name: 'AppInput',
  
  props: {
    modelValue: {
      type: [String, Number],
      default: ''
    },
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'text'
    },
    id: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    prefixIcon: {
      type: String,
      default: ''
    },
    suffixIcon: {
      type: String,
      default: ''
    },
    hint: {
      type: String,
      default: ''
    },
    rules: {
      type: [String, Object, Function],
      default: ''
    },
    validateOnInput: {
      type: Boolean,
      default: false
    },
    min: {
      type: [String, Number],
      default: undefined
    },
    max: {
      type: [String, Number],
      default: undefined
    },
    step: {
      type: [String, Number],
      default: undefined
    },
    maxLength: {
      type: Number,
      default: undefined
    },
    rows: {
      type: Number,
      default: 3
    },
    autocomplete: {
      type: String,
      default: 'off'
    }
  },
  
  emits: ['update:modelValue', 'focus', 'blur', 'change', 'keydown', 'enter'],
  
  setup(props, { emit }) {
    const {
      value: inputValue,
      errorMessage,
      handleBlur,
      handleChange,
      handleInput,
      meta
    } = useField(props.name, props.rules, {
      initialValue: props.modelValue,
      validateOnValueUpdate: props.validateOnInput
    });
    
    const isFocused = ref(false);
    const touched = computed(() => meta.touched);
    const isValid = computed(() => meta.valid);
    
    const containerClasses = computed(() => ({
      'app-input-with-prefix': props.prefixIcon || !!this.$slots.prefix,
      'app-input-with-suffix': props.suffixIcon || props.clearable || !!this.$slots.suffix
    }));
    
    const counterExceeded = computed(() => {
      return props.maxLength && inputValue.value?.length > props.maxLength;
    });
    
    const getAriaDescribedBy = computed(() => {
      const ids = [];
      if (errorMessage.value) ids.push(`${props.id}-error`);
      if (props.hint && !errorMessage.value) ids.push(`${props.id}-hint`);
      if (props.maxLength) ids.push(`${props.id}-counter`);
      return ids.length ? ids.join(' ') : undefined;
    });
    
    const onInput = (event) => {
      handleInput(event);
      emit('update:modelValue', event.target.value);
    };
    
    const onFocus = (event) => {
      isFocused.value = true;
      emit('focus', event);
    };
    
    const onBlur = (event) => {
      isFocused.value = false;
      handleBlur();
      emit('blur', event);
    };
    
    const onChange = (event) => {
      handleChange(event);
      emit('change', event);
    };
    
    const onKeydown = (event) => {
      emit('keydown', event);
    };
    
    const onEnter = (event) => {
      emit('enter', event);
    };
    
    const clearInput = () => {
      inputValue.value = '';
      emit('update:modelValue', '');
      if (this.$refs.input) {
        this.$refs.input.focus();
      }
    };
    
    return {
      inputValue,
      error: errorMessage,
      isFocused,
      touched,
      isValid,
      containerClasses,
      counterExceeded,
      getAriaDescribedBy,
      onInput,
      onFocus,
      onBlur,
      onChange,
      onKeydown,
      onEnter,
      clearInput
    };
  }
});
</script>

<style scoped>
.app-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.app-input-label {
  color: var(--text-color);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  display: flex;
  align-items: center;
  gap: var(--spacing-2xs);
}

.app-input-required-mark {
  color: var(--danger-color);
}

.app-input-container {
  position: relative;
  display: flex;
  align-items: center;
  background-color: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  transition: all var(--transition-fast) var(--transition-timing);
}

.app-input-container:hover:not(.app-input-disabled) {
  border-color: var(--primary-400);
}

.app-input-focused {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 4px var(--primary-100);
}

.app-input-disabled {
  background-color: var(--secondary-50);
  cursor: not-allowed;
}

.app-input-error {
  border-color: var(--danger-color);
}

.app-input-error:focus {
  box-shadow: 0 0 0 4px var(--danger-100);
}

.app-input-success {
  border-color: var(--success-color);
}

.app-input-success:focus {
  box-shadow: 0 0 0 4px var(--success-100);
}

.app-input {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  color: var(--text-color);
  font-size: var(--font-size-base);
  line-height: var(--line-height-base);
  background: none;
  border: none;
  outline: none;
}

.app-input::placeholder {
  color: var(--text-lighter);
}

.app-textarea {
  resize: vertical;
  min-height: 100px;
}

.app-input-prefix,
.app-input-suffix {
  display: flex;
  align-items: center;
  padding: 0 var(--spacing-sm);
  color: var(--text-light);
}

.app-input-with-prefix .app-input {
  padding-left: 0;
}

.app-input-with-suffix .app-input {
  padding-right: 0;
}

.app-input-icon {
  font-size: var(--font-size-lg);
}

.app-input-clear {
  cursor: pointer;
  padding: var(--spacing-2xs);
  color: var(--text-lighter);
  transition: color var(--transition-fast) var(--transition-timing);
}

.app-input-clear:hover {
  color: var(--danger-color);
}

.app-input-feedback {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 20px;
}

.app-input-hint {
  font-size: var(--font-size-xs);
  color: var(--text-light);
}

.app-input-error {
  font-size: var(--font-size-xs);
  color: var(--danger-color);
}

.app-input-counter {
  font-size: var(--font-size-xs);
  color: var(--text-light);
}

.app-input-counter-limit {
  color: var(--danger-color);
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity var(--transition-fast) var(--transition-timing);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Dark Mode Adjustments */
@media (prefers-color-scheme: dark) {
  .app-input-container {
    background-color: var(--surface-color);
  }
  
  .app-input-disabled {
    background-color: var(--secondary-800);
  }
  
  .app-input::placeholder {
    color: var(--text-light);
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .app-input-container,
  .app-input-clear,
  .fade-enter-active,
  .fade-leave-active {
    transition: none;
  }
}
</style> 